import type { Teacher } from "./LpKit";

const AVATAR = "https://static.classdojo.com/tutor/public/trial";
const LP_ASSET = "https://static.classdojo.com/tutor/public/lp";

export const STRIDE_GLANCE = [{ big: "1:1 & online" }, { big: "25 or 55 min" }, { big: "3–5×/week" }];

export const STRIDE_LENGTHS = [
  { value: "25" as const, label: "25 minutes" },
  { value: "55" as const, label: "55 minutes" },
];

export const STRIDE_PACKAGES = [
  {
    id: 3,
    price: 25,
    label: "3 days a week",
    tag: "Research minimum",
  },
  {
    id: 4,
    price: 23,
    label: "4 days a week",
    tag: "Most popular",
  },
  {
    id: 5,
    price: 22,
    label: "5 days a week",
    tag: "Fastest progress",
  },
];

export const STRIDE_EVIDENCE = [
  {
    stat: "2 to 3× the math learned in a year",
    body: "In randomized trials across Chicago public schools, students in daily tutoring learned two to three times as much math in a single year as their classmates.",
    source: "University of Chicago Education Lab — Saga/Match RCTs",
  },
  {
    stat: "Course failures cut nearly in half",
    body: "The same trials cut math course failures by roughly half, and lifted math grades.",
    source: "EdResearch for Recovery — high-dosage tutoring review",
  },
  {
    stat: "+3 to 15 months of progress, every year",
    body: "A meta-analysis of tutoring programs evaluated by randomized controlled trials found gains equivalent to 3 to 15 additional months of school.",
    source: "Nickow, Oreopoulos & Quan — American Educational Research Journal, 2024",
  },
  {
    stat: "3+ days a week",
    body: "The most effective programs run three or more sessions a week. Studies have found little evidence that once-a-week tutoring is enough.",
    source: "EdResearch for Recovery — Accelerating student learning with high-dosage tutoring",
  },
  {
    stat: "Certified teachers",
    body: "Tutoring delivered by certified teachers produces the largest impacts of any tutor type in the research.",
    source: "J-PAL evidence review, 2023",
  },
];

export const STRIDE_TUTOR_POINTS = [
  {
    lead: "A certified teacher, background-checked.",
    body: "Most have taught full classrooms for years.",
  },
  {
    lead: "You don't have to explain it yourself.",
    body: "Homework help is handled by someone qualified, so evenings aren't spent correcting math problems.",
  },
  {
    lead: "Someone they'll actually listen to.",
    body: "Kids take it differently from a teacher than from a parent, even word for word.",
  },
  {
    lead: "A note home after every class.",
    body: "What they mastered, what's next, one thing to ask them at dinner.",
  },
];

export const STRIDE_STEPS = [
  {
    step: "Step 1",
    title: "Choose your tutor",
    body: "Browse certified teachers by grade and subject, and pick the one that fits your child.",
    image: "https://static.classdojo.com/img/tutor/at-home.svg",
  },
  {
    step: "Step 2",
    title: "Try a class",
    body: "The tutor conducts an assessment and creates a learning plan with next steps.",
    image: "https://static.classdojo.com/img/tutor/in-school.svg",
  },
  {
    step: "Step 3",
    title: "Lock in the week",
    body: "Keep the same tutor and the same slots every week, and the classes run themselves.",
    image: "https://static.classdojo.com/img/tutor/push-in.svg",
  },
];

/** TODO: replace with real tutors + ratings from the tutor search API. */
export const STRIDE_TEACHERS: Teacher[] = [
  {
    name: "Ms. Reyes",
    grades: "Grades 3–5",
    rating: "4.9",
    reviews: "124",
    photo: `${LP_ASSET}/tutor_reyes.jpg`,
    bio: "I build strong foundations with clear, encouraging lessons tailored to how each student learns best.",
  },
  {
    name: "Mr. Okafor",
    grades: "Grades 6–9",
    rating: "4.8",
    reviews: "96",
    photo: `${LP_ASSET}/tutor_okafor.jpg`,
    bio: "I help students see the why behind math, strengthen core skills, and feel confident tackling new concepts.",
  },
  {
    name: "Ms. Bloom",
    grades: "Grades 1–4",
    rating: "5.0",
    reviews: "78",
    photo: `${LP_ASSET}/tutor_bloom.jpg`,
    bio: "I make early learning joyful and structured so young students grow their skills, curiosity, and confidence.",
  },
  {
    name: "Mr. Alvarez",
    grades: "Grades 9–12",
    rating: "4.9",
    reviews: "63",
    photo: `${LP_ASSET}/tutor_alvarez.jpg`,
    bio: "I challenge older students with focused instruction that closes gaps and builds lasting independent learning habits.",
  },
];

export const STRIDE_FEATURED_QUOTE = {
  tag: "Caught up nearly a year",
  quote:
    "From the very first session, Brittany identified my child's struggles and tailored her lessons... he has caught up nearly a year in his studies!",
  name: "Dojo Tutor parent",
  grade: "1st grade",
};

export const STRIDE_TESTIMONIALS = [
  {
    quote: "We did tutoring 5 times a week to keep him motivated and challenged. My son loved it.",
    name: "Dojo Tutor parent",
    grade: "5th grade",
    avatar: `${AVATAR}/avatar-jameikia-r@2x.jpg`,
  },
  {
    quote: "My daughter hated math and would avoid it altogether. Now she's at the top of her class.",
    name: "Dojo Tutor parent",
    grade: "5th grade",
    avatar: `${AVATAR}/avatar-vanessa-h@2x.jpg`,
  },
  {
    quote: "Ms Laura was wonderful with my autistic son. Really helped him get ahead in school.",
    name: "Dojo Tutor parent",
    grade: "4th grade",
    avatar: `${AVATAR}/avatar-julie-k@2x.jpg`,
  },
  {
    quote: "My son was behind and she helped him catch up FAST. She's a very patient and caring tutor.",
    name: "Dojo Tutor parent",
    grade: "6th grade",
    avatar: `${AVATAR}/avatar-lavitta-e@2x.jpg`,
  },
  {
    quote: "Mrs. Kelly is the highlight of my kid's day! He can't wait to see her after school. Very thankful for her.",
    name: "Dojo Tutor parent",
    grade: "2nd grade",
    avatar: `${AVATAR}/avatar-carolyn-a@2x.jpg`,
  },
];

export const STRIDE_FAQ = [
  {
    value: "why-three",
    question: "Why three days a week and not one?",
    answer:
      "Because that's what the evidence supports. High-dosage tutoring research looks at programs running at least three sessions a week with a consistent tutor; occasional sessions don't produce the same gains. That's why we sell tutoring as a weekly package rather than one-off classes.",
  },
  {
    value: "session",
    question: "What actually happens in a session?",
    answer:
      "In the first class, the tutor assesses your child's skill level and creates a lesson plan. Then each session is tailored to your child's needs and learning style.",
  },
  {
    value: "length",
    question: "How long are sessions?",
    answer:
      "Sessions are 25 or 55 minutes. Research suggests this range is the most effective for young learners based on brain development and average attention span. You pick a time that works for your family.",
  },
  {
    value: "who",
    question: "Who are Dojo tutors?",
    answer:
      "Every Dojo Tutor is a certified teacher, and most are experienced classroom teachers. Every tutor completes a rigorous selection process including a background check and a sample lesson.",
  },
  {
    value: "same-tutor",
    question: "Do we keep the same tutor?",
    answer:
      "Yes, that consistency is the point. If the match isn't right after the first class, you can try a different tutor before committing to a weekly package. You'll have the option to opt in to the substitute tutor program for when your tutor is out.",
  },
  {
    value: "skip",
    question: "What if we need to skip a week?",
    answer:
      "You can cancel classes any time with advance notice, and there's no long-term commitment. School holidays can be paused.",
  },
  {
    value: "cost",
    question: "What does it cost?",
    answer:
      "Tutors set their own prices. 25-minute 1-on-1 classes start at $25 each on the three-day plan and decrease in price when you get more classes per week. You're billed weekly, per child.",
  },
];
